import React from 'react';
import styled from 'styled-components';
import { easeDraw, easeEnter, easePop, mixHex, ramp } from './motion';

const INK = '#1c1c1c';
const TILE = '#f4f2f1';
const LINE_MUTED = '#d8d4d1';
const LINE_SOLID = '#c2bdb9';
const TEXT_MUTED = '#9b9794';

const BAND_X = 1;
const BAND_Y = 470;
const BAND_H = 260;
const CELL_W = 251;

/**
 * Crops the tall dashboard band to the region that carries the composition —
 * the accent rule, the cell headers and the full trend line. The cells run off
 * the bottom edge by design.
 */
export const VIEW = { x: 0, y: 464, width: 1280, height: 205 };

const CELL_GAP = 6;
const CELL_PAD = 20;
const BAR_W = 13;
const BAR_GAP = 7;

type Cell = {
  label: string;
  bars: [number, number, number];
  dx: number;
  dy: number;
  rot: number;
  x: number;
};

const CELLS: Cell[] = (
  [
    { label: 'INTAKE', bars: [16, 28, 20], dx: -104, dy: 72, rot: -5 },
    { label: 'BUILD', bars: [24, 12, 32], dx: 46, dy: 108, rot: 4 },
    { label: 'SHIP', bars: [12, 30, 24], dx: -32, dy: 134, rot: -7 },
    { label: 'RUN', bars: [28, 18, 34], dx: 80, dy: 78, rot: 6 },
    { label: 'GROW', bars: [14, 26, 36], dx: 126, dy: 116, rot: -4 },
  ] as Omit<Cell, 'x'>[]
).map((cell, i) => ({ ...cell, x: BAND_X + i * (CELL_W + CELL_GAP) }));

/** Every cell's bars share one baseline, set by the tallest bar in the band. */
const BARS_BASELINE =
  BAND_Y + 18 + Math.max(...CELLS.flatMap((cell) => cell.bars));

const TREND_POINTS: [number, number][] = [
  [-10, 628],
  [240, 648],
  [490, 658],
  [740, 606],
  [990, 552],
  [1180, 500],
];

const segmentLengths = (points: [number, number][]): number[] =>
  points.slice(1).map((point, i) => Math.hypot(point[0] - points[i][0], point[1] - points[i][1]));

const SEGMENTS = segmentLengths(TREND_POINTS);
const TREND_LENGTH = SEGMENTS.reduce((sum, length) => sum + length, 0);
const TREND_PATH = `M ${TREND_POINTS.map(([x, y]) => `${x} ${y}`).join(' L ')}`;

/** Point at `fraction` of the polyline's total arc length. */
const pointAt = (fraction: number): [number, number] => {
  let remaining = fraction * TREND_LENGTH;
  for (let i = 0; i < SEGMENTS.length; i++) {
    if (remaining <= SEGMENTS[i]) {
      const t = SEGMENTS[i] === 0 ? 0 : remaining / SEGMENTS[i];
      return [
        TREND_POINTS[i][0] + (TREND_POINTS[i + 1][0] - TREND_POINTS[i][0]) * t,
        TREND_POINTS[i][1] + (TREND_POINTS[i + 1][1] - TREND_POINTS[i][1]) * t,
      ];
    }
    remaining -= SEGMENTS[i];
  }
  return TREND_POINTS[TREND_POINTS.length - 1];
};

/* Hover lives in CSS so pointer tracking never re-runs the scene timeline. The
   cell stays anchored to the accent rule — only the bars move, growing from
   their baseline, so nothing detaches from the band. */
const CellGroup = styled.g`
  .cell-tile,
  .cell-label,
  .cell-bar {
    transition: fill 220ms ease, stroke 220ms ease;
  }

  .cell-bars {
    transform-box: fill-box;
    transform-origin: bottom;
    transition: transform 260ms cubic-bezier(0.22, 0.9, 0.24, 1);
  }

  &:hover .cell-tile {
    fill: #ffffff;
    stroke: var(--band-accent);
  }

  &:hover .cell-label {
    fill: var(--band-accent);
  }

  &:hover .cell-bar {
    fill: ${INK};
  }

  &:hover .cell-bar-accent {
    fill: var(--band-accent);
  }

  &:hover .cell-bars {
    transform: scaleY(1.22);
  }
`;

interface TrendBandProps {
  /** Per-cell 0..1 convergence into the grid. */
  tileProgress: number[];
  /** 0..1 transition from sketch to locked-in grid. */
  lock: number;
  /** 0..1 stroke reveal of the trend line. */
  draw: number;
  /** Camera scale applied to the whole band. */
  camera: number;
  accent: string;
}

const TrendBand: React.FC<TrendBandProps> = ({ tileProgress, lock, draw, camera, accent }) => {
  const solid = easePop(lock);
  const rule = easeDraw(ramp(lock, 0.1, 1));
  const head = pointAt(draw);
  const tip = easePop(ramp(draw, 0.9, 1));

  const rootStyle = {
    display: 'block',
    width: '100%',
    height: '100%',
    transform: `scale(${camera})`,
    transformOrigin: '50% 0%',
    '--band-accent': accent,
  } as React.CSSProperties;

  return (
    <svg
      viewBox={`${VIEW.x} ${VIEW.y} ${VIEW.width} ${VIEW.height}`}
      preserveAspectRatio="xMidYMin slice"
      aria-hidden="true"
      focusable="false"
      style={rootStyle}
    >
      <rect
        x={0}
        y={467}
        width={1280}
        height={3}
        fill={accent}
        pointerEvents="none"
        transform={`translate(640 0) scale(${rule} 1) translate(-640 0)`}
      />

      {CELLS.map((cell, i) => {
        const entered = easeEnter(tileProgress[i] ?? 1);
        const settle = 1 - entered;
        const centerX = cell.x + CELL_W / 2;
        const centerY = BAND_Y + BAND_H / 2;
        const barsRight = cell.x + CELL_W - CELL_PAD;
        const barsLeft = barsRight - (BAR_W * 3 + BAR_GAP * 2);

        return (
          <CellGroup
            key={cell.label}
            opacity={0.55 + 0.45 * entered}
            transform={
              `translate(${cell.dx * settle} ${cell.dy * settle}) ` +
              `rotate(${cell.rot * settle} ${centerX} ${centerY})`
            }
          >
            <g>
              <rect
                className="cell-tile"
                x={cell.x + 1}
                y={BAND_Y + 1}
                width={CELL_W - 2}
                height={BAND_H - 2}
                fill={TILE}
                stroke={mixHex(LINE_MUTED, LINE_SOLID, solid)}
                strokeWidth={2}
                strokeDasharray={solid > 0.5 ? undefined : '6 5'}
              />
              <text
                className="cell-label"
                x={cell.x + CELL_PAD}
                y={BAND_Y + CELL_PAD + 13}
                fill={mixHex(TEXT_MUTED, INK, solid)}
                fontSize={12}
                fontWeight={600}
                letterSpacing="1.92"
              >
                {cell.label}
              </text>
              <g className="cell-bars">
                {cell.bars.map((height, j) => (
                  <rect
                    key={cell.label + j}
                    className={j === 2 ? 'cell-bar cell-bar-accent' : 'cell-bar'}
                    x={barsLeft + j * (BAR_W + BAR_GAP)}
                    y={BARS_BASELINE - height}
                    width={BAR_W}
                    height={height}
                    fill={
                      j === 2
                        ? mixHex(LINE_MUTED, accent, solid)
                        : mixHex(LINE_MUTED, INK, solid * 0.45)
                    }
                  />
                ))}
              </g>
            </g>
          </CellGroup>
        );
      })}

      <path
        d={TREND_PATH}
        fill="none"
        stroke={accent}
        strokeWidth={6}
        strokeLinejoin="miter"
        strokeDasharray={TREND_LENGTH}
        strokeDashoffset={TREND_LENGTH * (1 - draw)}
        pointerEvents="none"
      />
      {draw > 0.01 && draw < 0.995 && (
        <rect
          x={head[0] - 7}
          y={head[1] - 7}
          width={14}
          height={14}
          fill={accent}
          pointerEvents="none"
        />
      )}
      {tip > 0 && (
        <g
          transform={`translate(1180 500) rotate(-15.3) scale(${tip})`}
          pointerEvents="none"
        >
          <path d="M 26 0 L -6 -14 L -6 14 Z" fill={accent} />
        </g>
      )}
    </svg>
  );
};

export default TrendBand;
